import React, { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import MenuBar from '../../components/MenuBar'
import Header from '../../components/Header'
import './CreateRentalContractPage.css'
import './RentalContractDetailPage.css'
import httpClient from '../../components/helpers/httpClient'
import { RentalContractForm } from './RentalContractForm.jsx'
import DocumentsSection from '../../components/DocumentsSection'
import { toast } from 'react-toastify'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import LoadingSpinner from '../../components/placeholders/LoadingSpinner'
import { showNotificationPopup } from '../../components/NotificationPopup'
import Button from '../../components/UI/Button'

// constants are defined in the shared form and not needed here

function RentalContractDetailPage() {
  const { contractId } = useParams()
  const navigate = useNavigate()

  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [expandedSection, setExpandedSection] = useState(null)

  const [initialData, setInitialData] = useState(null)
  const primaryUnitId = initialData?.units?.[0]?.id || null

  const fetchContract = async () => {
    if (!contractId) return
    setLoading(true)
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/rental-contracts/${contractId}?detailed=true`)
      if (response.status === 200) {
        setInitialData(response.data)
      } else {
        setInitialData(null)
        toast.error('Failed to load contract')
      }
    } catch (e) {
      setInitialData(null)
      toast.error('Failed to load contract')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchContract()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId])

  const formRef = useRef(null)

  const handleSave = async () => {
    setSaving(true)
    try {
      await formRef.current?.submit()
    } finally {
      setSaving(false)
    }
  }

  const handleGoBack = () => { navigate(-1) }

  const handleToggleSection = (sectionId) => {
    if (expandedSection === sectionId) setExpandedSection(null)
    else setExpandedSection(sectionId)
  }

  const handleDeleteContract = () => {
    if (!contractId) return

    showNotificationPopup({
      message: 'Are you sure you want to delete this rental contract? This action cannot be undone.',
      cancelButton: { text: 'Cancel', color: 'var(--secondairy-light-medium-gray)' },
      actionButton: { text: 'Delete', color: 'var(--red)' },
      onAction: async () => {
        setDeleting(true)
        try {
          await toast.promise(
            httpClient.delete(import.meta.env.VITE_API_URL + `/api/rental-contracts/${contractId}`),
            {
              pending: 'Deleting rental contract...',
              success: 'Rental contract deleted 👍',
              error: 'Delete failed 😢'
            }
          )
          navigate('/rental-contracts')
        } catch (error) {
          const apiMsg = error?.response?.data?.error || error?.response?.data?.message
          toast.error(apiMsg || 'Cannot delete rental contract.')
        } finally {
          setDeleting(false)
        }
      }
    })
  }

  return (
    <div className='template'>
      <Header>
        <div className="back_button" onClick={handleGoBack}><KeyboardBackspaceIcon /> Go Back</div>
        <div className='header__options--right'>
          <Button
            variant='primary'
            size='sm'
            onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
            disabled={loading || saving || deleting || !initialData}
            title={!initialData ? 'No contract loaded' : undefined}
          >
            {isEditing ? (saving ? 'SAVING...' : 'SAVE') : 'EDIT DETAILS'}
          </Button>
          <Button
            variant='secondary'
            tone='red'
            size='sm'
            onClick={handleDeleteContract}
            disabled={loading || saving || deleting || !initialData}
          >
            {deleting ? 'DELETING...' : 'DELETE'}
          </Button>
        </div>
      </Header>
      <MenuBar />

      <div className='template__body' id='body-rental-contract-detail'>
        {loading ? (
          <LoadingSpinner />
        ) : !initialData ? (
          <div className="no-data-message">
            <p>Rental contract not found or failed to load.</p>
          </div>
        ) : (
          <>
          <RentalContractForm
            ref={formRef}
            initialData={initialData}
            readOnly={!isEditing}
            splitSections={true}
            expandedSection={expandedSection}
            onToggleSection={handleToggleSection}
            onSubmit={async (payload) => {
              try {
                const response = await httpClient.put(import.meta.env.VITE_API_URL + `/api/rental-contracts/${contractId}`, payload)
                if (response.status === 200) {
                  toast.success('Contract updated')
                  setIsEditing(false)
                  await fetchContract()
                } else {
                  toast.error('Failed to update contract')
                }
              } catch (e) {
                toast.error('Failed to update contract')
              }
            }}
          />
          <div
            className={`body__section ${expandedSection === "rental-contract-documents" ? "full-screen" : ""}`}
            id="rental-contract-documents"
          >
            <h2 onClick={() => handleToggleSection("rental-contract-documents")}>DOCUMENTS</h2>
            {primaryUnitId ? (
              <DocumentsSection
                scope="unit"
                entityId={primaryUnitId}
                editable={true}
                rentalContractId={contractId}
                defaultTypeName="Rental contract"
                showRentalContractSelect={true}
              />
            ) : (
              <div className="no-data-message">
                <p>Link a unit to this rental contract before uploading documents.</p>
              </div>
            )}
          </div>
          </>
        )}
      </div>
    </div>
  )
}

export default RentalContractDetailPage


